import Close20 from '@carbon/icons-react/es/close/20';
import Search16 from '@carbon/icons-react/es/search/16';
import { ConfigProvider } from 'antd';
import _, { camelCase } from 'lodash';
import React, { useCallback, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { UserStatus } from 'api/models';
import { CpBody02Placeholder } from 'components/atoms/customer-portal/typography';
import { Grid } from 'components/atoms/grid';
import { Table } from 'components/molecules/table';
import { SearchInput } from 'components/organisms/customer-portal/components/cp-input';
import { useGetBackground } from 'components/organisms/customer-portal/types/utils';
import { GetUserNameWithProjectOwnerAndTimeOffIcons } from 'components/organisms/module-settings/team-members/list/table-components';
import { FilterableField } from 'components/organisms/tasks/fields';
import {
  getRenderer,
  getValueFormatter,
} from 'components/organisms/tasks/renderers';
import { getValueGetter } from 'components/organisms/tasks/value-getter';
import { User } from 'data/members/api';
import { useAccount } from 'hooks/use-account';
import { useTranslate } from 'hooks/use-translation';
import { tryEmojify } from 'utils/emojify';
import { fullName } from 'utils/full-name';
import { tryGetTextFromHTML } from 'utils/getTextFromHTML';
import { useUserFieldsAsFilterableFields } from 'utils/user-fields';
import { CpSectionWrapper } from '../../common';
import { CpWidgetHeader } from '../../common/header';
import { CpTeamMemberWidgetProps } from '../cp-teamMember';
import { useSortProjectMembers } from './utils';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-around;
  align-items: flex-start;
  overflow-y: hidden;
`;

const TableWrapper = styled.div`
  width: 100%;
  max-width: 100%;
  overflow-x: auto;
`;

export const StyledGrid = styled(Grid)`
  .ag-row {
    border-bottom: 1px solid var(--table-border-color);
  }
  .ag-overlay-no-rows-wrapper {
    display: flex;
    text-align: unset;
    pointer-events: auto;
  }
  .ag-pinned-left-cols-container {
    padding: 0 !important;
  }
  .ag-cell-last-left-pinned > .ag-react-container {
    align-self: stretch;
    width: 100%;
    > div {
      width: 100%;
    }
  }

  .ag-center-cols-container {
    .ag-row {
      .ag-cell:first-child {
        cursor: default;
        .ag-react-container {
          justify-content: space-between;
          align-items: center;
          width: 100%;
          height: 100%;
        }
      }
    }
  }
`;
const EmptyStateMessage = styled(CpBody02Placeholder)`
  padding: var(--cp-spacing-08) 0;
`;

export const CpTeamMemberList: React.FC<CpTeamMemberWidgetProps> = ({
  disabled,
  data,
  widget,
  projectId,
}) => {
  const { company } = useAccount();

  const background = useGetBackground(widget);
  const translate = useTranslate();
  const [search, setSearch] = useState('');
  const onSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>): void => {
      setSearch(e.target.value);
    },
    [],
  );
  const onSearchClear: React.MouseEventHandler<React.ReactSVGElement> =
    useCallback((e) => {
      e.preventDefault();
      e.stopPropagation();
      setSearch('');
    }, []);
  const { sortProjectMembers } = useSortProjectMembers({ projectId });
  const sortedMembers = useMemo(
    () => sortProjectMembers(data?.teamMembers ?? []),
    [data?.teamMembers, sortProjectMembers],
  );
  const filteredMemberData = useMemo(
    () =>
      sortedMembers.filter(
        (user) =>
          user.company.companyId === company.companyId &&
          user.userStatus === UserStatus.Active &&
          (search.length > 0
            ? fullName(user).toLowerCase().includes(search.trim().toLowerCase())
            : true),
      ) ?? [],
    [company.companyId, search, sortedMembers],
  );

  const { fields: userFields } = useUserFieldsAsFilterableFields({
    hidePartnerField: true,
  });

  const chosenFields = useMemo(() => {
    const fields: string[] = [];

    data?.teamMembers?.map((user) => {
      user?.fields?.map((field) => {
        if (field.fieldName && !fields.includes(field.fieldName)) {
          fields.push(field.fieldName);
        }

        return '';
      });

      return '';
    });

    return fields;
  }, [data?.teamMembers]);

  /*
    chosenField's name is of the format: "FieldName_ID"
    userField's name is of the format: "fieldNameID"
    Inorder to make the comparison meaningfull transorming chosenField's name to userField's name format.
    Not really sure if this is the ideal way to go about it, but have a pressing customer issue raised on this,
    so will send this as a temprorary fix anx make sure to come back and have this corrected.
    Issue: https://rocketlane.slack.com/archives/C051J9RK36W/p1727464764337099
  */
  const mappedFields: (FilterableField | undefined)[] = useMemo(() => {
    return (
      chosenFields?.map((fieldLabel) => {
        const formattedChosenFieldFieldLabel = _.lowerFirst(fieldLabel).replace(
          '_',
          '',
        );

        return userFields?.find((_f) => {
          return (
            _f?.fieldName === formattedChosenFieldFieldLabel ||
            camelCase(_f?.fieldName)?.toLowerCase() ===
              camelCase(formattedChosenFieldFieldLabel)?.toLowerCase()
          );
        });
      }) ?? []
    ).filter((f) => !!f);
  }, [chosenFields, userFields]);

  const columnWidth = 100 / (mappedFields?.length + 2);

  const getFieldValue = ({ user, field }) => {
    const fieldValueForUser = user.fields.find(
      (_field) => _field.fieldId === field.fieldId,
    );

    return tryGetTextFromHTML(
      tryEmojify((fieldValueForUser?.fieldValue as string) ?? '-'),
    );
  };

  const _columns = useMemo(() => {
    return mappedFields.map((field) => {
      return {
        title: field?.fieldLabel,
        dataIndex: field?.fieldName,
        width: `${columnWidth}%`,
        cellRenderer: getRenderer(field as FilterableField),
        valueGetter: getValueGetter(field as FilterableField),
        valueFormatter: getValueFormatter(field as FilterableField),
        cellRendererParams: { translate },
        render: (text, user: User) => {
          return getFieldValue({ user, field }) ?? '-';
        },
      };
    });
  }, [columnWidth, mappedFields, translate]);

  const columns = [
    {
      title: 'Name',
      dataIndex: 'Name',
      width: `${columnWidth}%`,
      render: (text, p) => {
        return (
          <GetUserNameWithProjectOwnerAndTimeOffIcons
            data={p}
            projectId={projectId}
          />
        );
      },
    },
    {
      title: 'Role',
      dataIndex: 'Role',
      width: `${columnWidth}%`,
      render: (text, user: User) => {
        return user?.role?.roleName ?? '-';
      },
    },
    ..._columns,
  ];

  return (
    <CpSectionWrapper
      paddingY={widget?.widgetDefinition?.styles?.paddingY as number}
      isDisabled={disabled}
      background={background}
      data-builder-mode-full-tile="true"
    >
      <Wrapper>
        <CpWidgetHeader>
          <CpWidgetHeader.Title autoAdjustColor id="hero-title">
            {data?.title}
          </CpWidgetHeader.Title>
          <CpWidgetHeader.Description autoAdjustColor>
            {data?.description}
          </CpWidgetHeader.Description>
        </CpWidgetHeader>
        <SearchInput
          data-cy="cp.team_members.list.search"
          placeholder={translate('search')}
          prefix={<Search16 />}
          value={search}
          onChange={onSearchChange}
          suffix={search ? <Close20 onClick={onSearchClear} /> : undefined}
        />
        <TableWrapper>
          <ConfigProvider
            renderEmpty={() => (
              <EmptyStateMessage>
                <FormattedMessage id="customer_portal.team_members.empty_state.title" />
              </EmptyStateMessage>
            )}
          >
            <Table
              dataSource={filteredMemberData as any}
              columns={columns}
              pagination={false}
              scroll={{
                y: '376px',
              }}
            />
          </ConfigProvider>
        </TableWrapper>
      </Wrapper>
    </CpSectionWrapper>
  );
};
